// Facebook SDK utility for Meta Business Extension integration

// Track whether FB.init() has actually been called successfully
let fbInitialized = false;
let fbInitPromise = null;

export const initializeFacebookSDK = () => {
  // Return existing promise if initialization is already in progress or done
  if (fbInitPromise) return fbInitPromise;

  fbInitPromise = new Promise((resolve, reject) => {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    console.log('Initializing Facebook SDK with App ID:', appId);

    if (!appId || appId === 'your_facebook_app_id_here') {
      const error = 'Facebook App ID not configured properly in .env file';
      console.error(error);
      fbInitPromise = null;
      reject(new Error(error));
      return;
    }

    // Already fully initialized, resolve immediately
    if (fbInitialized && window.FB) {
      resolve(window.FB);
      return;
    }

    const doInit = () => {
      try {
        window.FB.init({
          appId: appId,
          cookie: true,
          xfbml: true,
          version: 'v25.0'
        });
        fbInitialized = true;
        console.log('Facebook SDK initialized successfully');
        window.FB.AppEvents.logPageView();
        resolve(window.FB);
      } catch (error) {
        console.error('Error initializing Facebook SDK:', error);
        fbInitPromise = null;
        reject(error);
      }
    };

    // Timeout after 10 seconds
    const timeoutId = setTimeout(() => {
      console.warn('Facebook SDK failed to initialize within 10 seconds.');
      fbInitPromise = null;
      reject(new Error('Facebook SDK failed to initialize within timeout'));
    }, 10000);

    const clearAndDo = () => {
      clearTimeout(timeoutId);
      doInit();
    };

    if (window.FB) {
      // Script already loaded, init immediately
      clearAndDo();
    } else {
      // Script not yet loaded, set up fbAsyncInit callback
      window.fbAsyncInit = function() {
        console.log('Facebook SDK fbAsyncInit called');
        clearAndDo();
      };

      // Load the SDK script if not already present
      if (!document.getElementById('facebook-jssdk')) {
        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/en_US/sdk.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    }
  });

  return fbInitPromise;
};

export const checkFacebookLoginStatus = () => {
  return new Promise((resolve) => {
    if (!window.FB || !fbInitialized) {
      resolve({ status: 'not_loaded' });
      return;
    }

    window.FB.getLoginStatus((response) => {
      resolve(response);
    });
  });
};

export const getFacebookLoginUrl = (redirectUri, state) => {
  const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
  const defaultRedirectUri = process.env.REACT_APP_REDIRECT_URI;
  const scope = 'whatsapp_business_management,business_management,whatsapp_business_manage_events';

  const params = new URLSearchParams({
    client_id: appId,
    redirect_uri: redirectUri || defaultRedirectUri,
    scope: scope,
    response_type: 'code',
    state: state,
    display: 'popup'
  });

  return `https://www.facebook.com/v25.0/dialog/oauth?${params.toString()}`;
};

export const getRedirectUri = () => {
  return process.env.REACT_APP_REDIRECT_URI || window.location.origin;
};

// Embedded Signup configuration id (created in the Meta App Dashboard with the
// WhatsApp Embedded Signup / coexistence feature enabled).
export const getFacebookConfigId = () => process.env.REACT_APP_FACEBOOK_CONFIG_ID;

/**
 * Launch WhatsApp Embedded Signup.
 *
 * A single Meta-hosted flow that covers all onboarding paths: creating a new Cloud API
 * number, selecting an existing WABA, and coexistence (connecting an existing WhatsApp
 * Business app number via QR scan). Meta hosts the account-selection UI; we capture the
 * resulting waba_id + phone_number_id from the WA_EMBEDDED_SIGNUP window messages and the
 * authorization code from the FB.login callback. The code is exchanged for a token
 * server-side (the client never handles a long-lived token).
 *
 * @returns {Promise<{code: string, wabaId: string, phoneNumberId: string|null, accountType: string}>}
 * @see https://developers.facebook.com/docs/whatsapp/embedded-signup/custom-flows
 */
/**
 * Classic Facebook OAuth login returning a user access token.
 *
 * The pre-Embedded-Signup connect path: the token is sent to the backend, which
 * discovers the WABAs it can access (POST /api/whatsapp/discover) and later stores
 * it for the chosen WABA. Works with standard access for app-role users, so it is
 * usable while Embedded Signup onboarding is still gated behind Meta Advanced Access.
 *
 * @returns {Promise<{accessToken: string, expiresIn: number}>}
 */
export const loginWithFacebookForToken = async () => {
  const FB = await initializeFacebookSDK();

  return new Promise((resolve, reject) => {
    FB.login(
      (response) => {
        if (response.status === 'connected' && response.authResponse?.accessToken) {
          resolve({
            accessToken: response.authResponse.accessToken,
            expiresIn: response.authResponse.expiresIn
          });
        } else {
          reject(new Error('Facebook login failed or was cancelled'));
        }
      },
      {
        scope: 'whatsapp_business_management,business_management,whatsapp_business_manage_events',
        auth_type: 'rerequest'
      }
    );
  });
};

export const launchEmbeddedSignup = async () => {
  const FB = await initializeFacebookSDK();
  const configId = getFacebookConfigId();

  if (!configId) {
    throw new Error('Embedded Signup is not configured. Set REACT_APP_FACEBOOK_CONFIG_ID in your .env file.');
  }

  return new Promise((resolve, reject) => {
    // The waba_id / phone_number_id arrive via window messages (separate from the
    // FB.login callback that delivers the auth code), so capture them as they come in.
    // ES emits several messages during the flow, only keep ones that actually carry a
    // waba_id so a later progress event can't wipe out the good capture.
    let sessionData = null;
    let cancelError = null;

    const messageHandler = (event) => {
      if (typeof event.origin !== 'string' ||
          !(event.origin.endsWith('facebook.com') || event.origin.endsWith('whatsapp.com'))) {
        return;
      }

      // FB may post the payload as a JSON string or as an already-parsed object.
      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          return; // not a JSON Embedded Signup message
        }
      }

      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
        return;
      }

      console.log('[EmbeddedSignup] message:', payload);

      if (payload.event === 'CANCEL' || payload.event === 'ERROR') {
        cancelError = payload.data?.error_message || 'Embedded Signup was cancelled';
        return;
      }

      const data = payload.data || {};
      const wabaId = data.waba_id || data.wabaId;
      if (!wabaId) {
        return; // intermediate/progress event without account ids, keep waiting
      }
      sessionData = {
        wabaId,
        phoneNumberId: data.phone_number_id || data.phoneNumberId || null,
        // The business-app-onboarding finish event signals a coexistence connection.
        accountType: payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
          ? 'coexistence'
          : 'production'
      };
    };

    window.addEventListener('message', messageHandler);
    const cleanup = () => window.removeEventListener('message', messageHandler);

    FB.login(
      (response) => {
        const code = response?.authResponse?.code;

        // The session-info message can land just after this callback, give it a brief
        // window to arrive before deciding nothing came back.
        const settle = (attempt = 0) => {
          if (cancelError) {
            cleanup();
            reject(new Error(cancelError));
            return;
          }
          if (sessionData?.wabaId) {
            cleanup();
            resolve({
              code,
              wabaId: sessionData.wabaId,
              phoneNumberId: sessionData.phoneNumberId,
              accountType: sessionData.accountType || 'production'
            });
            return;
          }
          if (!code) {
            cleanup();
            reject(new Error('Embedded Signup did not complete (no authorization code returned)'));
            return;
          }
          if (attempt < 30) {
            setTimeout(() => settle(attempt + 1), 100); // wait up to ~3s for the session-info message
            return;
          }
          cleanup();
          reject(new Error('Embedded Signup completed but no WhatsApp Business Account was returned'));
        };

        settle();
      },
      {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          sessionInfoVersion: '3'
        }
      }
    );
  });
};

// Utility to check if we're in a secure context (required for Facebook SDK)
export const isSecureContext = () => {
  return window.location.protocol === 'https:' ||
         window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

export const validateFacebookAppConfig = () => {
  const appId = process.env.REACT_APP_FACEBOOK_APP_ID;

  if (!appId) {
    console.error('Facebook App ID not configured. Please set REACT_APP_FACEBOOK_APP_ID in your .env file.');
    return false;
  }

  if (!isSecureContext()) {
    console.error('Facebook SDK requires HTTPS or localhost. Current protocol:', window.location.protocol);
    return false;
  }

  return true;
};
